import React from "react";
import c from "classnames";
import Tagify from "@yaireo/tagify";
import { Link } from "react-router-dom";
import { BrowseVideo } from "../byteark/BrowseVideo";

export function Details({
  program,
  categoryList,
  channelList,
  tagList,
  videoKey,
  onThumbnailChange,
  onCoverPhotoChange,
  onSubmit,
  onUploadVideo,
}) {
  return (
    <div className="my-8">
      <Header></Header>
      <Form
        onThumbnailChange={onThumbnailChange}
        onCoverPhotoChange={onCoverPhotoChange}
        categoryList={categoryList}
        channelList={channelList}
        tagList={tagList}
        onSubmit={onSubmit}
        onUploadVideo={onUploadVideo}
        program={program}
        videoKey={videoKey}
      ></Form>
    </div>
  );
}

export function Header() {
  return (
    <div className="pb-5 border-b border-gray-200">
      <h3 className="text-lg leading-6 font-medium text-gray-900">
        Edit Program
      </h3>
    </div>
  );
}

export function Form({
  onThumbnailChange,
  onCoverPhotoChange,
  categoryList,
  channelList,
  tagList,
  onSubmit,
  program,
  videoKey,
  onUploadVideo,
}) {
  const [thumbnail, setThumbnail] = React.useState(null);
  const [coverPhoto, setCoverPhoto] = React.useState(null);
  const [selectedCategory, setSelectedCategory] = React.useState(null);
  const defaultChannel = React.useMemo(
    () => ({
      id: -1,
      name: "ไม่ระบุ",
    }),
    []
  );
  const [selectedChannel, setSelectedChannel] = React.useState(defaultChannel);
  const [tagify, setTagify] = React.useState(null);
  const [mainStory, setMainStory] = React.useState(null);
  const [shortStory, setShortStory] = React.useState(null);
  const [releasedDate, setReleasedDate] = React.useState(null);
  const [formValues, setFormValues] = React.useState({
    title: "",
    author: "",
    broadcastTime: "",
    description: "",
    trailerLink: "",
  });
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (program && channelList.length) {
      setFormValues({
        title: program.title,
        author: program.author,
        broadcastTime: program.broadcast_time,
        description: program.description,
        trailerLink: program.youtube,
        metaDescription: program.meta_description,
        keywords: program.keywords,
      });
      setThumbnail(program.thumbnail);
      setCoverPhoto(program.banner);
      setSelectedCategory(program.program_category);
      setSelectedChannel(
        channelList.find((ch) => ch.id === program.broadcast_channel) ||
          defaultChannel
      );
    }
  }, [program, channelList, defaultChannel]);

  React.useEffect(() => {
    if (shortStory?.html && mainStory?.html) {
      shortStory.html.set(program.short_story);
      mainStory.html.set(program.main_story);
    }
  }, [program, shortStory, mainStory]);

  React.useEffect(() => {
    if (program && tagify) {
      tagify.addTags(program.program_tag.map((t) => t.tag));
    }
  }, [program, tagify]);

  React.useEffect(() => {
    if (program?.released_date && releasedDate) {
      releasedDate.setDate(new Date(program.released_date));
    }
  }, [program, releasedDate]);

  const handleFormValueChange = (name) => (event) => {
    setFormValues({
      ...formValues,
      [name]: event.target.value,
    });
  };

  const isValid = () => {
    return formValues.title && tagify.value.length > 4 && selectedCategory;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (isValid()) {
      const values = {
        ...formValues,
        mainStory: mainStory.html.get(),
        shortStory: shortStory.html.get(),
        byteark: videoKey,
        thumbnail,
        coverPhoto,
        category: selectedCategory?.id,
        channel: selectedChannel?.id > 0 ? selectedChannel.id : null,
        tags: tagify.value.map((v) => v.value),
        releasedDate: releasedDate.selectedDates
          ? releasedDate.selectedDates[0]?.toISOString()
          : null,
      };
      setError(null);
      onSubmit(values);
    } else {
      setError([
        formValues.title ? null : "Title is required.",
        selectedCategory ? null : "Category is required",
        tagify.value.length > 4 ? null : "Requires at least 5 tags.",
      ]);
    }
  };

  const handleFileChange = (handler, resolve) => {
    return async (e) => {
      if (e.target.files[0]) {
        const file = e.target.files[0];
        const result = await handler(file);
        resolve(result);
      }
    };
  };

  React.useEffect(() => {
    const editorOptions = {
      key:
        "re1H1qB4C1D7A5E5D5hedxD-16kB-11vF3C-8mrC7upvaF3sD3kfgwD1D1D1F1F1I4B8B1B6E5==",
      attribution: false,
      imageUploadParam: "image",
      imageUploadURL: "/program-static-api/images/",
      imageUploadMethod: "POST",
      events: {
        "image.inserted": function ($img) {
          $img[0].style.width = "600px";
        },
      },
    };
    setMainStory(new window.FroalaEditor("#mainStory", editorOptions));
    setShortStory(new window.FroalaEditor("#shortStory", editorOptions));
  }, [mainStory, shortStory]);

  React.useEffect(() => {
    const tagOptions = tagList.map(({ name }) => ({
      value: name,
    }));
    if (!tagify) {
      const tagsInput = document.querySelector("#tags-input");
      const t = new Tagify(tagsInput, {
        whitelist: tagOptions,
        dropdown: {
          maxItems: 50,
          classname: "tags-look",
          enabled: 0,
          closeOnSelect: true,
        },
        originalInputValueFormat: (valuesArr) =>
          valuesArr.map((item) => item.value).join(","),
      });
      setTagify(t);
    } else {
      tagify.settings.whitelist.splice(0, tagOptions.length, ...tagOptions);
    }
  }, [tagList, tagify]);

  React.useEffect(() => {
    const datePickerOptions = {
      enableTime: true,
      defaultHour: 0,
      time_24hr: true,
    };

    setReleasedDate(window.flatpickr("#releasedDate", datePickerOptions));
  }, []);

  return (
    <div className="mt-8">
      <div>
        <div>
          <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
            <div className="sm:col-span-6">
              <label
                htmlFor="title"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Title
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="title"
                  name="title"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.title}
                  onChange={handleFormValueChange("title")}
                />
              </div>
              <p className="mt-2 text-sm text-gray-500">Required</p>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="author"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Author
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="author"
                  name="author"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.author}
                  onChange={handleFormValueChange("author")}
                />
              </div>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="broadcastTime"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Broadcast Time
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="broadcastTime"
                  name="broadcastTime"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.broadcastTime}
                  onChange={handleFormValueChange("broadcastTime")}
                />
              </div>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="description"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Description
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="description"
                  name="description"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.description}
                  onChange={handleFormValueChange("description")}
                />
              </div>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="trailerBytearkLink"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Trailer Byteark Link
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <BrowseVideo
                  videoKey={videoKey}
                  onUploadVideo={onUploadVideo}
                />
              </div>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="trailerLink"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Trailer Youtube Link
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="trailerLink"
                  name="trailerLink"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.trailerLink}
                  onChange={handleFormValueChange("trailerLink")}
                />
              </div>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="thumbnail"
                className="block text-sm leading-5 font-medium text-gray-700"
              >
                Thumbnail
              </label>
              <div className="mt-2 flex items-center">
                <span>
                  <input
                    type="file"
                    className="text-sm leading-4 font-medium text-gray-700 hover:text-gray-500 focus:outline-none"
                    accept="image/*"
                    onChange={handleFileChange(onThumbnailChange, setThumbnail)}
                  ></input>
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Recommended resolution: 620x380px
              </p>
            </div>
            <div className="sm:col-span-4">
              {thumbnail ? (
                <img className="w-full" src={thumbnail} alt="Thumbnail" />
              ) : null}
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="coverPhoto"
                className="block text-sm leading-5 font-medium text-gray-700"
              >
                Cover Photo
              </label>
              <div className="mt-2 flex items-center">
                <span>
                  <input
                    type="file"
                    className="text-sm leading-4 font-medium text-gray-700 hover:text-gray-500 focus:outline-none"
                    accept="image/*"
                    onChange={handleFileChange(
                      onCoverPhotoChange,
                      setCoverPhoto
                    )}
                  ></input>
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Recommended resolution: 1280x459px
              </p>
            </div>
            <div className="sm:col-span-4">
              {coverPhoto ? (
                <img className="w-full" src={coverPhoto} alt="Cover" />
              ) : null}
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="shortStory"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Short Story
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <textarea
                  id="shortStory"
                  name="shortStory"
                  rows={3}
                  className="form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  defaultValue={""}
                />
              </div>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="mainStory"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Main Story
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <textarea
                  id="mainStory"
                  name="mainStory"
                  rows={6}
                  className="form-textarea block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  defaultValue={""}
                />
              </div>
            </div>
            <div className="sm:col-span-6">
              <label className="block text-sm font-medium leading-5 text-gray-700">
                Category
              </label>
              <div className="mt-1">
                <span className="relative z-0 inline-flex shadow-sm rounded-md">
                  {categoryList.map((cat, i) => {
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        className={c(
                          "relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150",
                          {
                            "rounded-l-md": i === 0,
                            "-ml-px": i > 0,
                            "rounded-r-md": i === categoryList.length - 1,
                            "bg-orange-500 text-white":
                              cat.id === selectedCategory?.id,
                            "bg-white text-gray-700":
                              cat.id !== selectedCategory?.id,
                          }
                        )}
                        onClick={() => setSelectedCategory(cat)}
                      >
                        {cat.title}
                      </button>
                    );
                  })}
                </span>
              </div>
              <p className="mt-2 text-sm text-gray-500">Required</p>
            </div>
            <div className="sm:col-span-6">
              <label className="block text-sm font-medium leading-5 text-gray-700">
                Broadcast Channel
              </label>
              <div className="mt-1">
                <span className="relative z-0 inline-flex shadow-sm rounded-md">
                  {[defaultChannel, ...channelList].map((ch, i) => {
                    return (
                      <button
                        key={ch.id}
                        type="button"
                        className={c(
                          "relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm leading-5 font-medium hover:text-gray-500 focus:z-10 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-100 active:text-gray-700 transition ease-in-out duration-150",
                          {
                            "rounded-l-md": i === 0,
                            "-ml-px": i > 0,
                            "rounded-r-md": i === channelList.length,
                            "bg-orange-500 text-white":
                              ch.id === selectedChannel?.id,
                            "bg-white text-gray-700":
                              ch.id !== selectedChannel?.id,
                          }
                        )}
                        onClick={() => setSelectedChannel(ch)}
                      >
                        {ch.name}
                      </button>
                    );
                  })}
                </span>
              </div>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="tags-input"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Tags
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input id="tags-input" name="tags" />
              </div>
              <p className="mt-2 text-sm text-gray-500">
                Requires at least 5 tags
              </p>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="metaDescription"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Meta Description
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="metaDescription"
                  name="metaDescription"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.metaDescription}
                  onChange={handleFormValueChange("metaDescription")}
                />
              </div>
            </div>
            <div className="sm:col-span-6">
              <label
                htmlFor="keywords"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                meta keywords
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="keywords"
                  name="keywords"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                  value={formValues.keywords}
                  onChange={handleFormValueChange("keywords")}
                />
              </div>
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="releasedDate"
                className="block text-sm font-medium leading-5 text-gray-700"
              >
                Released Date
              </label>
              <div className="mt-1 rounded-md shadow-sm">
                <input
                  id="releasedDate"
                  name="releasedDate"
                  className="form-input block w-full transition duration-150 ease-in-out sm:text-sm sm:leading-5"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8 border-t border-gray-200 pt-5">
        <div className="flex justify-end">
          <span className="inline-flex rounded-md shadow-sm">
            <Link
              to="/"
              className="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
            >
              Cancel
            </Link>
          </span>
          <span className="ml-3 inline-flex rounded-md shadow-sm">
            <button
              type="button"
              className={c(
                "inline-flex justify-center py-2 px-4 border border-transparent text-sm leading-5 font-medium rounded-md focus:outline-none bg-orange-500 hover:bg-orange-400 text-white focus:border-orange-600 focus:shadow-outline-orange active:bg-orange-600 transition duration-150 ease-in-out"
              )}
              onClick={handleSubmit}
            >
              Save
            </button>
          </span>
        </div>
      </div>
      {error ? <ErrorBlock error={error}></ErrorBlock> : null}
    </div>
  );
}

function ErrorBlock({ error }) {
  const items = error.filter((item) => item);
  return (
    <div className="rounded-md bg-red-50 p-4 mt-4">
      <div className="flex">
        <div className="flex-shrink-0">
          {/* Heroicon name: x-circle */}
          <svg
            className="h-5 w-5 text-red-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clipRule="evenodd"
            />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm leading-5 font-medium text-red-800">
            There were {items.length} error(s) with your submission
          </h3>
          <div className="mt-2 text-sm leading-5 text-red-700">
            <ul className="list-disc pl-5">
              {items.map((item, i) => {
                return (
                  <li key={i} className={c({ "mt-1": i > 0 })}>
                    {item}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
